import { useState } from "react";
import CreateHeygenVideoView from "@features/tools/view/heygen-video-view/create-heygen-video-view";
import { useToast } from "@packages/contexts/ToastContext";
import { VideoAvatarCreateRequestDto } from "@packages/api/madlenAPI.schemas";
import {
  useCreateVideoAvatar,
  useGetVideoAvatar,
} from "@packages/api/madlenAPI";
import { useTranslation } from "react-i18next";
import HeygenVideoCard from "@features/tools/components/heygen-video-generator/HeygenVideoCard";
import ErrorComponent from "@features/tools/components/error-component";
import LoadingContainer from "@packages/components/Loading";
import { ArrowLeft } from "lucide-react";
import { Button } from "@core/button";
import { useNavigate } from "react-router-dom";

const CreateHeygenVideoGenerator = () => {
  const [error, setError] = useState<Error | null>(null);
  const [isTranscriptLoading, setIsTranscriptLoading] = useState(false);
  const { addToast } = useToast();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const {
    mutateAsync: createVideoAvatar,
    isPending: isCreatingHeygenVideo,
    data: heygenAvatarCreateResponse,
  } = useCreateVideoAvatar();

  const { data: heygenAvatar } = useGetVideoAvatar(
    heygenAvatarCreateResponse?.id ?? 0,
    {
      query: {
        enabled: !!heygenAvatarCreateResponse?.id,
        refetchInterval: (query) => {
          if (query.state.data?.status === "READY") {
            return false;
          }
          if (query.state.data?.transcript) {
            setIsTranscriptLoading(false);
          }
          return 3000;
        },
      },
    },
  );

  const handleSubmit = async (input: VideoAvatarCreateRequestDto) => {
    setError(null);
    setIsTranscriptLoading(true);
    try {
      await createVideoAvatar({
        data: input,
      });

      return Promise.resolve();
    } catch (err: any) {
      console.error("Error creating video avatar:", err);
      setError(err as Error);
      setIsTranscriptLoading(false);
      addToast({
        title: t("heygenVideoGenerator.error.title"),
        variant: "error",
        duration: 5000,
      });
      return Promise.reject(err);
    }
  };

  if (isCreatingHeygenVideo || isTranscriptLoading) {
    return (
      <LoadingContainer
        title={t("heygenVideoGenerator.generateLoading")}
        themeColor={"purple"}
      />
    );
  }

  if (error) {
    return (
      <ErrorComponent
        title={t("heygenVideoGenerator.error.title")}
        description={t("heygenVideoGenerator.error.description")}
      />
    );
  }

  if (heygenAvatar?.transcript) {
    return (
      <div className="relative">
        <div className="container max-w-4xl mx-auto px-4 py-8">
          <Button
            onClick={() => navigate(-1)}
            variant="solid"
            size="md"
            textColor="text-white dark:text-white"
            bgColor="bg-gray-400 dark:bg-gray-700"
            rounded="lg"
            className="mb-2"
          >
            <ArrowLeft size={24} />
          </Button>
          <div className="w-full h-0 mb-8 sm:mb-0" />
          <HeygenVideoCard avatar={heygenAvatar} />
        </div>
      </div>
    );
  }
  return (
    <CreateHeygenVideoView
      onSubmit={handleSubmit}
      isLoading={isCreatingHeygenVideo}
    />
  );
};

export default CreateHeygenVideoGenerator;
